/**
 * Home + Ask Led greeting — how the headline is decided.
 *
 * Design record for the "personal greeting" (shipped 2026-08-25; brainstorm +
 * rationale in ledai_knowledge/2026-08-25-job-rhythm-greeting-design.md).
 *
 * WHAT IT'S FOR
 * The greeting should make a superintendent feel "this app knows my job" —
 * not "knows me" (Claude-style "Leo returns!"), not a status line, not generic
 * warmth. So the copy speaks to the rhythm of site work: pre-dawn starts, the
 * Friday close-out, weekend shifts, late daily-log nights, and coming back
 * after time away — framed as coming back *on site*.
 *
 * HOW IT'S CHOSEN — `pickGreeting`
 * A FIXED priority ladder with ONE string per rung; the first rung whose
 * condition holds wins. Nothing is random. Same situation ⇒ same words, every
 * time, on every surface.
 *
 *   #  condition                                        headline
 *   1  never walked (sites loaded, all visitCount 0)    Welcome
 *   2  app last opened ≥ 14 days ago                    Leo's back.  (one line; "Welcome back." without a name)
 *   3  app last opened ≥ 3 days ago                     Back on site
 *   4  03:00–05:59                                      Early start
 *   5  19:00–02:59                                      Good evening
 *   6  Saturday / Sunday                                Weekend shift
 *   7  Monday before noon                               Fresh week
 *   8  Friday                                           Happy Friday
 *   9  otherwise                                        Good morning / afternoon / evening
 *
 * Why this order: return (2–3) beats everything because it's the rarest and
 * most "noticed" moment. Time-of-day extremes (4–5) beat weekday rungs (6–8)
 * because "Early start" at 4am on a Friday is the more specific truth. 3am is
 * the night/morning boundary (a super starting a pre-dawn shift gets "Early
 * start"; someone finishing a daily log at 1am is closing out, so 00:00–02:59
 * is still "Good evening"). Weekend can't overlap Mon/Fri.
 *
 * Why deterministic: Claude's app picks randomly from ~50 eligible strings —
 * that's the cheap way to *fake* noticing. We have real signals a chatbot
 * doesn't, so we only ever say something true about the moment; a random pool
 * can lose "Happy Friday" to a plain "Good morning".
 *
 * COPY CONSTRAINT
 * Every headline must fit ONE 40px/800-weight line on an iPhone SE (~15
 * chars incl. the trailing comma). That's why there is no sub-line and no
 * "Last walk Tue at Riverside" — the primary card already owns site context.
 *
 * SIGNALS (no backend, no shared types, no native)
 *  - msSinceLastSeen — `greetingGapMs`: localStorage "ledai:lastSeen:<uid>"
 *    read ONCE per launch, the gap pinned in sessionStorage, lastSeen
 *    stamped = now. Later calls in the same launch get the pinned gap, so
 *    "Back on site" doesn't flip to "Good morning" while the user is looking
 *    at it (the WebView remounts pages on tab switches). sessionStorage dies
 *    with the WebView, so the next cold open measures again.
 *  - hasEverWalked — `hasEverWalkedFrom(sites, loaded)`: any site with
 *    visitCount > 0 or a lastVisitAt. Works offline off the projects snapshot.
 *  - name — `greetingName`: first word of displayName (or username).
 *  - The clock is the DEVICE's local time, not settings.timezone — a phone in
 *    another timezone greets for where it is.
 *
 * UNKNOWN INPUTS NEVER MAKE A CLAIM
 * Every signal has an "unknown" state and each unknown falls through:
 *  - hasEverWalked === null (sites still loading) skips rung 1 — a hydrating
 *    page must never greet a veteran with "Welcome";
 *  - msSinceLastSeen === null (fresh install, storage unavailable) skips 2–3;
 *  - name === "" drops the name line, so nothing renders a placeholder like
 *    "Good morning, there". The name arrives late on three real paths: cold
 *    launch off the cached projects snapshot (session === null until the WS
 *    snapshot lands), offline launch (the settings proxy is empty), and a
 *    failed SettingsManager.hydrate() (displayName stays null).
 *
 * RENDERING
 * `pickGreeting` returns { line1, line2 }. Two-line form: "Early start," over
 * "Leo."; one-line form (line2 === null) when there's no name or when the
 * headline already contains the name ("Leo's back." — never "Leo's back," /
 * "Leo."). Home header (HomePage.tsx) and Ask Led (AskPage.tsx) both render
 * this exact pair — they used to disagree before 5am when each had its own
 * hour table, which is why the helper exists at all.
 *
 * CHANGING THE COPY
 * Edit the ladder in `pickGreeting` and the matching case in greeting.test.ts
 * (one test per rung + every overlap). Keep each string ≤ ~15 chars, plain
 * and trade-spoken, and only claim what a signal proves.
 */
/**
 * Greeting for a local-clock hour. Shared by the home header and Ask Led so the
 * two never disagree — they used to split before 5am (home "Good evening", Ask
 * "Good morning"). 3am is the night/morning boundary: superintendents starting
 * a pre-dawn shift get "Good morning", anyone still up at 2am gets "Good
 * evening".
 *
 * Note the hour is the DEVICE's local clock (`new Date().getHours()`), not
 * `settings.timezone` — a phone in another timezone greets for where it is.
 */
export function greetingForHour(hour: number): string {
  if (hour < 3) return "Good evening";
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

/** First name for the greeting, or "" when settings haven't synced yet. */
export function greetingName(
  displayName?: string | null,
  username?: string | null,
): string {
  const raw = (displayName || username || "").trim();
  if (!raw) return "";
  return raw.split(/\s+/)[0];
}

// The ladder itself. The full decision record is the docstring at the top of
// this file — keep the table there in sync with the rungs below.

export interface GreetingInputs {
  now: Date;
  /** ms since the app was last opened; null when unknown. See greetingGapMs. */
  msSinceLastSeen: number | null;
  /** null while job sites haven't loaded — rung 1 is skipped, not guessed. */
  hasEverWalked: boolean | null;
  /** First name, or "" when settings haven't synced (greetingName). */
  name: string;
}

/**
 * Rendered as `line1` over `line2`. Two-line form is "<headline>," / "<Name>.";
 * one-line form (no name, or a headline that already contains the name) has
 * `line2 === null`.
 */
export interface GreetingLines {
  line1: string;
  line2: string | null;
}

const DAY_MS = 86_400_000;
export const RETURN_LONG_DAYS = 14;
export const RETURN_SHORT_DAYS = 3;

export function pickGreeting(i: GreetingInputs): GreetingLines {
  const withName = (headline: string): GreetingLines =>
    i.name ? { line1: `${headline},`, line2: `${i.name}.` } : { line1: `${headline}.`, line2: null };

  if (i.hasEverWalked === false) return withName("Welcome");

  const gap = i.msSinceLastSeen;
  if (gap !== null && gap >= RETURN_LONG_DAYS * DAY_MS) {
    return i.name
      ? { line1: `${i.name}'s back.`, line2: null }
      : { line1: "Welcome back.", line2: null };
  }
  if (gap !== null && gap >= RETURN_SHORT_DAYS * DAY_MS) return withName("Back on site");

  const hour = i.now.getHours();
  const day = i.now.getDay();
  if (hour >= 3 && hour < 6) return withName("Early start");
  if (hour >= 19 || hour < 3) return withName("Good evening");
  if (day === 0 || day === 6) return withName("Weekend shift");
  if (day === 1 && hour < 12) return withName("Fresh week");
  if (day === 5) return withName("Happy Friday");
  return withName(greetingForHour(hour));
}

/** True once sites are loaded and any of them has been walked; null until loaded. */
export function hasEverWalkedFrom(
  sites: ReadonlyArray<{ visitCount?: number; lastVisitAt?: Date | string | null }> | null,
  loaded: boolean,
): boolean | null {
  if (!loaded || sites === null) return null;
  return sites.some((s) => (s.visitCount ?? 0) > 0 || !!s.lastVisitAt);
}

/**
 * ms since this user last opened the app, measured ONCE per app launch.
 *
 * Reads `localStorage["ledai:lastSeen:<userId>"]` the first time it's asked
 * in a launch, pins the answer in sessionStorage, then stamps lastSeen = now.
 * Every later call in the same launch (tab switches, remounts, Ask Led) gets
 * the pinned gap, so "Back on site" doesn't flip to "Good morning" while the
 * user is looking at it. sessionStorage dies with the WebView, so the next
 * cold open measures again. Null on first launch / no storage → no absence.
 */
export function greetingGapMs(userId: string, nowMs: number = Date.now()): number | null {
  if (!userId) return null;
  if (typeof window === "undefined") return null;
  const gapKey = `ledai:greetingGap:${userId}`;
  const seenKey = `ledai:lastSeen:${userId}`;
  try {
    const pinned = window.sessionStorage?.getItem(gapKey);
    if (pinned !== null && pinned !== undefined) {
      return pinned === "" ? null : Number(pinned);
    }
    const seenRaw = window.localStorage?.getItem(seenKey);
    const seen = seenRaw ? Number(seenRaw) : NaN;
    const gap = Number.isFinite(seen) && seen <= nowMs ? nowMs - seen : null;
    window.sessionStorage?.setItem(gapKey, gap === null ? "" : String(gap));
    window.localStorage?.setItem(seenKey, String(nowMs));
    return gap;
  } catch {
    return null;
  }
}
